import { AtlBrowserStorageLocal } from '@atlassian/browser-storage-controls/web-storage-service';
import { isNativeMobile, setUserAgent } from '@confluence/browser-helper';
import { fg } from '@confluence/feature-gating';
import { getLogger } from '@confluence/logger';

// Must call this before any browser-helper functions, to ensure user agent has been set first
setUserAgent();

const logger = getLogger('mobile-detection');

// Copied from com.atlassian.confluence.mobile.MobileUtils
const NATIVE_IOS_REGEX_STRING = 'iPhone;|iPad;|iPhone Simulator;|iPod;|iPod touch;';
const NATIVE_ANDROID_REGEX_STRING = 'Linux; U; Android|Android';
const MOBILE_USER_AGENT_REGEX = new RegExp(
	`(${NATIVE_IOS_REGEX_STRING}|${NATIVE_ANDROID_REGEX_STRING})`,
	'i',
);
const ANDROID_CHROME = /Chrome\/[.\d]* Mobile/i;
const OLD_ANDROID = /Linux; U; Android (?:[23]\.\d|4\.0\.[12])/i;
const NATIVE_IOS = new RegExp(`(${NATIVE_IOS_REGEX_STRING})`, 'i');
const NATIVE_ANDROID = new RegExp(`(${NATIVE_ANDROID_REGEX_STRING})`, 'i');

export const MOBILE_WEB_QUERY_OVERRIDE = 'mobileWebTestingMode=testing';

// Export for testing
export const MOBILE_WEB_DESKTOP_OVERRIDE_ACTIVE = 'mobile.web.desktop.override.active';

export const isMobileUserAgent = (): boolean => {
	// Port from com.atlassian.confluence.mobile.MobileUtils#isMobileViewRequest
	const ua = window.navigator.userAgent;

	return (
		!!ua &&
		!OLD_ANDROID.test(ua) &&
		(MOBILE_USER_AGENT_REGEX.test(ua) || ANDROID_CHROME.test(ua) || isNativeMobile())
	);
};

export const isDesktopOverrideActive = (): boolean => {
	if (fg('confluence_mobile_detection_storage_compliance')) {
		return AtlBrowserStorageLocal.getItem(MOBILE_WEB_DESKTOP_OVERRIDE_ACTIVE) === 'true';
	}
	return window.localStorage.getItem(MOBILE_WEB_DESKTOP_OVERRIDE_ACTIVE) === 'true';
};

export const activateDesktopOverride = (): void => {
	if (fg('confluence_mobile_detection_storage_compliance')) {
		try {
			// eslint-disable-next-line check-set-storage-usage/no-unimported-storage-set
			void AtlBrowserStorageLocal.setItem(MOBILE_WEB_DESKTOP_OVERRIDE_ACTIVE, 'true');
		} catch (e) {
			logger.error`Unable to set local storage. key = "${MOBILE_WEB_DESKTOP_OVERRIDE_ACTIVE}", value = "true": ${e}`;
		}
		return;
	}
	try {
		// eslint-disable-next-line check-set-storage-usage/no-unimported-storage-set
		window.localStorage.setItem(MOBILE_WEB_DESKTOP_OVERRIDE_ACTIVE, 'true');
	} catch (e) {
		logger.error`Unable to set local storage. key = "${MOBILE_WEB_DESKTOP_OVERRIDE_ACTIVE}", value = "true": ${e}`;
	}
};

export const deactivateDesktopOverride = (): void => {
	if (fg('confluence_mobile_detection_storage_compliance')) {
		AtlBrowserStorageLocal.removeItem(MOBILE_WEB_DESKTOP_OVERRIDE_ACTIVE);
		return;
	}
	window.localStorage.removeItem(MOBILE_WEB_DESKTOP_OVERRIDE_ACTIVE);
};

export function shouldShowMobileWeb(): boolean {
	// for testing purposes, query param forces mobile web
	if (window.location.search.includes(MOBILE_WEB_QUERY_OVERRIDE)) {
		return true;
	}

	return isMobileUserAgent() && !isDesktopOverrideActive();
}

export const isNativeAndroid = (): boolean => {
	const ua = window.navigator.userAgent;

	return !!ua && NATIVE_ANDROID.test(ua);
};

export const isNativeIOS = (): boolean => {
	const ua = window.navigator.userAgent;

	return !!ua && NATIVE_IOS.test(ua);
};
